{
  "general": {
    "noPermission": "You do not have permission to view system settings.",
    "loading": "Loading general settings",
    "versionLabel": "Version",
    "lastEditedBy": "Last edited by {{name}}",
    "reload": "Reload",
    "platformIdentity": {
      "title": "Platform identity",
      "description": "Branding shown across the admin, merchant, and vendor portals."
    },
    "platformName": {
      "label": "Platform name",
      "hint": "2 to 100 characters."
    },
    "logo": {
      "label": "Logo",
      "helper": "PNG / JPEG / SVG ≤ 5 MB"
    },
    "favicon": {
      "label": "Favicon",
      "helper": "ICO / PNG / SVG ≤ 5 MB"
    },
    "localeTime": {
      "title": "Locale & time",
      "description": "Defaults applied when a user has not set their personal preference."
    },
    "defaultLocale": {
      "label": "Default locale"
    },
    "defaultTimezone": {
      "label": "Default timezone (IANA)"
    },
    "dateFormat": {
      "label": "Default date format",
      "options": {
        "vi": "DD/MM/YYYY (Vietnamese)",
        "us": "MM/DD/YYYY (US)",
        "iso": "YYYY-MM-DD (ISO 8601)"
      }
    },
    "sessionTimeout": {
      "label": "Session timeout (minutes)",
      "hint": "Between 5 and 480 minutes."
    },
    "maintenance": {
      "title": "Maintenance mode",
      "description": "Toggling maintenance live is gated behind dual approval. Edits here only stage the configuration — task 50 owns the activate / deactivate workflow.",
      "bannerActive": "Maintenance is currently flagged ACTIVE in the staged config — submit via the dual-approval workflow before it takes effect.",
      "bannerInactive": "Maintenance is not active. Staged toggles will not deploy without the dual-approval workflow.",
      "activeLabel": "Active (staged)",
      "optionInactive": "Inactive",
      "optionActive": "Active (requires approval)",
      "messageLabel": "Banner message",
      "messagePlaceholder": "We are upgrading the system — back shortly.",
      "durationLabel": "Estimated duration (hours, 0-168)",
      "bypassLabel": "Bypass IPs (comma-separated)",
      "bypassPlaceholder": "10.0.0.1, 192.168.1.42"
    }
  },
  "merchantConfigs": {
    "noPermission": "You do not have permission to view system settings.",
    "loading": "Loading merchant configs",
    "versionLabel": "Version",
    "lastEditedBy": "Last edited by {{name}}",
    "reload": "Reload",
    "invalidJsonFallback": "Invalid JSON",
    "draftSignup": {
      "title": "Draft & signup behaviour",
      "description": "Lifecycle defaults applied to every newly created merchant."
    },
    "autoCancel": {
      "label": "Auto-cancel draft after (minutes)",
      "hint": "0 disables auto-cancellation of draft merchants."
    },
    "survey": {
      "label": "Registration survey ID",
      "placeholder": "(leave empty to disable the survey)"
    },
    "tier": {
      "title": "Tier assignment",
      "description": "Window over which merchant volume is rolled up to compute tier membership."
    },
    "tierWindow": {
      "label": "Tier evaluation window",
      "lifetime": "Lifetime",
      "lastDays": "Last {{days}} days"
    },
    "tierRules": {
      "label": "Tier rules (JSON array)",
      "parseError": "JSON parse error: {{message}}",
      "hint": "Each entry is { \"tier\": \"...\", \"criteria\": { ... } }."
    },
    "creditRules": {
      "title": "Default credit rules",
      "description": "Free-form JSON record. Validated server-side per the merchant namespace schema.",
      "parseError": "JSON parse error: {{message}}"
    },
    "atRisk": {
      "title": "Merchant At-Risk thresholds",
      "description": "Trigger thresholds that promote a merchant into the At-Risk cohort surfaced on the SuperAdmin dashboard.",
      "lowRating": "Low rating below",
      "rejection": "High rejection above (%)",
      "complaintCount": "Complaint spike (count)",
      "complaintWindow": "Complaint window (hours)"
    }
  },
  "orderConfigs": {
    "loading": "Loading Order settings…",
    "loadErrorTitle": "Failed to load Order settings",
    "loadErrorFallback": "Failed to load Order settings — please try again.",
    "saveErrorFallback": "Failed to save Order settings — please try again.",
    "retry": "Try again",
    "heading": "Order Configs",
    "description": "Configure FX rates, invoice numbering, auto-push, and order SLA escalation thresholds. Each Save writes an audit log + bumps the version.",
    "conflict": {
      "title": "Version has changed",
      "fallback": "Settings were updated by someone else while you were editing.",
      "reloadHint": "Please reload the page and try again.",
      "reload": "Reload from server"
    },
    "saved": "Saved (version {{version}}).",
    "fx": {
      "legend": "FX rate",
      "sourceLabel": "Rate source",
      "spreadLabel": "Spread (%) — margin added on top of the base rate"
    },
    "fxSourceOptions": {
      "ecb": "ECB (European Central Bank)",
      "xe": "XE.com",
      "manual": "Manual (admin entry)"
    },
    "invoice": {
      "legend": "Invoice numbering",
      "patternLabel": "Numbering pattern (tokens: YYYY, MM, DD, XXXXX)",
      "patternValid": "Valid pattern — must start with \"INV-\".",
      "patternInvalid": "Invalid pattern — must start with \"INV-\".",
      "previewLabel": "Preview:",
      "cronLabel": "Invoice auto-generate cron (5-field cron format)"
    },
    "autoPush": {
      "legend": "Auto-push & SLA escalation",
      "delayLabel": "Auto-push delay (seconds) — wait time before sending the order to the vendor",
      "slaLabel": "Low-stock escalation SLA (hours) — default 48h",
      "shippingZonesLabel": "Shipping zones link (URL — external zone management page, optional)"
    },
    "capacity": {
      "legend": "Capacity alert thresholds (default 80/95/100)",
      "low": "Low (early warning)",
      "warning": "Warning",
      "critical": "Critical"
    },
    "dirtyIndicator": "Unsaved changes",
    "versionIndicator": "Version {{version}}",
    "discard": "Discard changes",
    "save": "Save Changes",
    "saving": "Saving…"
  },
  "vendorConfigs": {
    "noPermission": "You do not have permission to view system settings.",
    "loading": "Loading vendor configs",
    "versionLabel": "Version",
    "lastEditedBy": "Last edited by {{name}}",
    "reload": "Reload",
    "payouts": {
      "title": "Payouts",
      "description": "Cadence and per-tier hold periods that vendor payouts honour.",
      "scheduleLabel": "Payout schedule"
    },
    "payoutScheduleOptions": {
      "weekly_friday": "Weekly (Friday)",
      "biweekly": "Bi-weekly",
      "monthly_first": "Monthly (1st)",
      "monthly_last": "Monthly (last day)",
      "on_demand": "On demand"
    },
    "holdTier": {
      "legend": "Hold period by tier (days)",
      "empty": "No hold tiers configured. Use \"Add tier\" to seed tier1=7 / tier2=14 / tier3=21 defaults (per PLAN).",
      "remove": "Remove",
      "add": "Add tier"
    },
    "capacity": {
      "title": "Capacity alerts",
      "description": "Utilization thresholds at which the platform surfaces capacity warnings on the SuperAdmin and vendor dashboards.",
      "low": "Low (%)",
      "warning": "Warning (%)",
      "critical": "Critical (%)"
    },
    "atRisk": {
      "title": "Vendor At-Risk thresholds",
      "description": "Same shape as the merchant At-Risk panel — these gates trigger the SuperAdmin vendor-at-risk widget.",
      "lowRating": "Low rating below",
      "highRejection": "High rejection above (%)",
      "complaintCount": "Complaint spike (count)",
      "complaintWindow": "Complaint window (hours)"
    },
    "lowStock": {
      "title": "Low-stock escalation SLA",
      "description": "How long the system waits before escalating a vendor low-stock alert to the SuperAdmin queue.",
      "slaLabel": "Escalation SLA (hours, 1-168, default 48)"
    },
    "slaThresholds": {
      "title": "Default SLA thresholds",
      "descBefore": "Free JSON map of stage → minutes (e.g.",
      "descAfter": ").",
      "jsonLabel": "default_sla_thresholds (JSON)",
      "parseError": "JSON parse error: {{message}}",
      "invalidJson": "Invalid JSON"
    }
  },
  "notificationsTab": {
    "loading": "Loading Notifications settings…",
    "loadErrorTitle": "Failed to load Notifications settings",
    "loadError": "Could not load Notifications settings — please try again.",
    "retry": "Retry",
    "heading": "Notifications",
    "description": "Configure the event × channel matrix, quiet hours (DND), daily digest, and gateway providers (SMS/Email/Push). Per, secret keys are rotated in the Security tab.",
    "versionConflictTitle": "Version changed",
    "versionConflictDefault": "Settings were updated by someone else while you were editing.",
    "versionConflictSuffix": "Please reload the page and try again.",
    "reload": "Reload from server",
    "saveErrorFallback": "Failed to save Notifications settings — please try again.",
    "saved": "Saved (version {{version}}).",
    "matrix": {
      "legend": "Event catalog × Channel matrix",
      "eventHeader": "Event",
      "cellAria": "{{event}} via {{channel}}"
    },
    "channels": {
      "in_app": "In-app",
      "email": "Email",
      "push": "Push",
      "sms": "SMS",
      "slack": "Slack",
      "lark": "Lark"
    },
    "quietHours": {
      "legend": "Quiet hours (DND) + Daily digest",
      "start": "Quiet hours start",
      "end": "Quiet hours end",
      "timezone": "Timezone (IANA)",
      "digestEnable": "Enable daily digest (bundle the day's notifications)",
      "digestTime": "Send time (local)",
      "timezonePlaceholder": "Asia/Bangkok"
    },
    "gateways": {
      "legend": "Gateway providers",
      "enable": "Enable",
      "note": "Note: API keys/secrets are rotated in the Security tab — this tab only toggles gateways on/off.",
      "twilio": "Twilio (SMS gateway)",
      "vonage": "Vonage (backup SMS gateway)",
      "sendgrid": "SendGrid (Email gateway)",
      "fcm": "FCM (Push notification)"
    },
    "dirty": {
      "unsaved": "Unsaved changes",
      "version": "Version {{version}}"
    },
    "actions": {
      "discard": "Discard changes",
      "saving": "Saving…",
      "save": "Save Changes"
    }
  },
  "support": {
    "loading": "Loading Support settings…",
    "loadErrorTitle": "Failed to load Support settings",
    "loadError": "Could not load Support settings — please try again.",
    "retry": "Retry",
    "heading": "Support",
    "description": "Configure SLA per priority, canned responses, auto-assign, and default categories for support tickets.",
    "versionConflictTitle": "Version changed",
    "versionConflictDefault": "Settings were updated by someone else while you were editing.",
    "versionConflictSuffix": "Please reload the page and try again.",
    "reload": "Reload from server",
    "saveErrorFallback": "Failed to save Support settings — please try again.",
    "saved": "Saved (version {{version}}).",
    "sla": {
      "legend": "SLA per priority — unit: minutes",
      "priorityHeader": "Priority",
      "firstResponseHeader": "First response (minutes)",
      "resolutionHeader": "Resolution (minutes)",
      "validationError": "Each SLA must be a positive integer (> 0 minutes)."
    },
    "priorities": {
      "critical": "Critical",
      "high": "High",
      "medium": "Medium",
      "low": "Low"
    },
    "canned": {
      "legend": "Canned responses",
      "empty": "No canned responses yet — click \"Add\" to create one.",
      "title": "Title",
      "remove": "Remove",
      "body": "Content (HTML — rich editor is upgraded in the page shell)",
      "add": "+ Add canned response"
    },
    "autoAssign": {
      "legend": "Auto-assign config",
      "maxOpen": "Max open tickets per admin",
      "workingStart": "Working hours start",
      "workingEnd": "Working hours end",
      "timezone": "Timezone (IANA)",
      "timezonePlaceholder": "Asia/Bangkok"
    },
    "categories": {
      "legend": "Default ticket categories",
      "list": "Category list (comma-separated)"
    },
    "dirty": {
      "unsaved": "Unsaved changes",
      "version": "Version {{version}}"
    },
    "actions": {
      "discard": "Discard changes",
      "saving": "Saving…",
      "save": "Save Changes"
    }
  },
  "emailTemplates": {
    "title": "Email Templates",
    "subtitle": "Manage system email templates (Auth / Orders / Billing / System / …) with EN / VI / FR i18n.",
    "create": "Create template",
    "filters": {
      "categoryLabel": "Category",
      "allCategories": "All",
      "searchLabel": "Search by name",
      "searchPlaceholder": "Order Confirmation…"
    },
    "columns": {
      "name": "Name",
      "category": "Category",
      "subjectEn": "Subject (EN)",
      "languages": "Languages",
      "lastUpdated": "Last Updated",
      "actions": "Actions"
    },
    "loading": "Loading…",
    "empty": "No templates yet — click “{{action}}” to get started.",
    "rowActions": {
      "edit": "Edit",
      "delete": "Delete"
    },
    "confirmDelete": "Delete template “{{name}}”? This will hide it from the list.",
    "toast": {
      "deletedTitle": "Template deleted",
      "deleteFailedTitle": "Failed to delete template"
    },
    "badge": {
      "complete": "{{loc}} complete",
      "missing": "{{loc}} missing"
    },
    "categoryLabels": {
      "Auth": "Auth",
      "Orders": "Orders",
      "Billing": "Billing",
      "System": "System",
      "Vendor": "Vendor",
      "Merchant": "Merchant",
      "Store": "Store",
      "Support": "Support"
    }
  },
  "emailTemplateEditor": {
    "untitledTemplate": "template",
    "subjectLabel": "Subject ({{locale}})",
    "bodyLabel": "Body ({{locale}})",
    "bodyAriaLabel": "Email body {{locale}}",
    "previewTextLabel": "Preview text",
    "footer": {
      "preview": "Preview",
      "testSend": "Test send"
    },
    "variables": {
      "heading": "Variables",
      "keyAriaLabel": "Variable key",
      "keyPlaceholder": "order_id",
      "descAriaLabel": "Variable description",
      "descPlaceholder": "Order public ID"
    },
    "categoryLabels": {
      "Auth": "Auth",
      "Orders": "Orders",
      "Billing": "Billing",
      "System": "System",
      "Vendor": "Vendor",
      "Merchant": "Merchant",
      "Store": "Store",
      "Support": "Support"
    }
  },
  "profile": {
    "card": {
      "title": "Account information",
      "description": "Update your name, phone number, timezone and avatar. All changes are recorded in the audit log."
    },
    "fields": {
      "firstName": "First name",
      "lastName": "Last name",
      "phone": "Phone number",
      "phonePlaceholder": "+84xxxxxxxxx",
      "timezone": "Timezone",
      "avatarUrl": "Avatar URL",
      "avatarUrlPlaceholder": "https://…",
      "language": "Language"
    },
    "success": "Changes saved.",
    "buttons": {
      "save": "Save changes",
      "saving": "Saving…"
    }
  },
  "changePassword": {
    "card": {
      "title": "Change password",
      "description": "This is a sensitive action — you may be asked to sign in again after updating."
    },
    "rules": {
      "len": "At least 12 characters",
      "upper": "At least 1 uppercase letter",
      "num": "At least 1 digit",
      "special": "At least 1 special character"
    },
    "validation": {
      "currentRequired": "Please enter your current password",
      "newMin": "Password must be at least 12 characters",
      "newUpper": "At least 1 uppercase letter required",
      "newNum": "At least 1 digit required",
      "newSpecial": "At least 1 special character required",
      "confirmRequired": "Please confirm your password",
      "mismatch": "Password confirmation does not match",
      "sameAsCurrent": "New password must differ from the current one"
    },
    "fields": {
      "current": "Current password",
      "new": "New password",
      "confirm": "Confirm new password"
    },
    "rulesAria": "Password requirements",
    "errors": {
      "invalid": "Current password is incorrect or the session has expired.",
      "reused": "This password was used recently. Please choose a different one.",
      "generic": "Request failed (HTTP {{status}})",
      "unknown": "Unknown error"
    },
    "success": "Password changed successfully.",
    "buttons": {
      "update": "Update password",
      "updating": "Updating..."
    }
  },
  "billingInfo": {
    "card": {
      "title": "Business",
      "description": "Sensitive fields — recent authentication required (fresh perms — a session re-authenticated within the last 5 minutes). The system refreshes automatically if the session has expired."
    },
    "fields": {
      "businessName": "Business name",
      "taxId": "Tax ID",
      "billingEmail": "Billing email",
      "line1": "Address (line 1)",
      "city": "City",
      "postalCode": "Postal code",
      "country": "Country"
    },
    "address": {
      "legend": "Billing address"
    },
    "countries": {
      "VN": "Vietnam",
      "US": "United States",
      "SG": "Singapore",
      "TH": "Thailand",
      "JP": "Japan",
      "GB": "United Kingdom",
      "FR": "France"
    },
    "errors": {
      "needAtLeastOne": "Please enter at least one field to update."
    },
    "success": "Billing information saved.",
    "buttons": {
      "save": "Save information",
      "saving": "Saving…"
    }
  },
  "preferences": {
    "card": {
      "title": "Appearance & Notifications",
      "description": "Theme, language and notification channels. The theme is saved both in localStorage and the database (cross-device persistence — synced across your devices)."
    },
    "fields": {
      "theme": "Theme",
      "language": "Language"
    },
    "themeOptions": {
      "light": "Light",
      "dark": "Dark",
      "system": "System"
    },
    "notifications": {
      "legend": "Notifications",
      "emailDigest": "Receive summary emails",
      "inApp": "In-app (bell in the top bar)",
      "emailPerEvent": "Email for each event",
      "sms": "SMS"
    },
    "success": "Preferences saved.",
    "buttons": {
      "save": "Save preferences",
      "saving": "Saving…"
    }
  },
  "securityCenter": {},
  "setup2fa": {
    "card": {
      "title": "Account security",
      "description": "Choose a 2FA method — TOTP (via an app like Google Authenticator) or Email OTP. Sensitive action — recent authentication within 5 minutes required."
    },
    "fields": {
      "method": "Method",
      "code": "Enter the 6-digit code"
    },
    "methodOptions": {
      "totp": "TOTP (App)",
      "email": "Email OTP"
    },
    "buttons": {
      "disable": "Disable 2FA",
      "processing": "Processing…",
      "startEnroll": "Start setup",
      "cancel": "Cancel",
      "verify": "Verify code",
      "verifying": "Verifying…",
      "done": "Done",
      "download": "Download codes",
      "copy": "Copy",
      "copied": "Copied"
    },
    "enroll": {
      "totpHint": "Scan the QR code with a TOTP app, or enter the secret manually:",
      "qrAlt": "2FA TOTP QR code",
      "secretLabel": "Secret: {{secret}}",
      "emailHintBefore": "An OTP code was sent to ",
      "emailFallback": "your email",
      "emailHintAfter": ". The code expires in 10 minutes."
    },
    "recovery": {
      "successBold": "2FA enabled successfully.",
      "note": " Save the recovery codes below in a safe place — they are shown only once."
    },
    "disabled": {
      "message": "2FA disabled. Re-enable it soon to protect your account."
    },
    "errors": {
      "impersonateSetup": "2FA cannot be set up during an impersonation session.",
      "impersonateDisable": "2FA cannot be disabled during an impersonation session.",
      "wrongPassword": "Password is incorrect."
    },
    "status": {
      "enabledTitle": "Two-factor authentication is on",
      "enabledDesc": "Your account is protected by 2FA. You can disable it below if needed."
    },
    "disableConfirm": {
      "passwordLabel": "Enter your password to disable 2FA",
      "passwordPlaceholder": "Current password"
    },
    "trustedDevices": {
      "title": "Trusted devices",
      "description": "Devices you chose to trust skip the 2FA code at login. Revoke any you don't recognize.",
      "current": "This device",
      "revoke": "Revoke",
      "unknownDevice": "Unknown device",
      "empty": "No trusted devices."
    }
  },
  "roles": {
    "noPermBefore": "You don't have permission to view the role list (requires ",
    "noPermAfter": "). Contact a Super Admin to be granted access.",
    "listErrorFallback": "Unable to load the role list",
    "header": {
      "title": "Roles & Permissions",
      "subtitle": "Manage roles + the permission matrix for admin/super admin (System > Roles)."
    },
    "createCta": "+ Create Custom Role",
    "toast": {
      "noPermTitle": "Insufficient permissions",
      "noPermDesc": "Only a Super Admin can create a custom role.",
      "createdTitle": "Role created",
      "createdDesc": "The new role has been added to the list."
    },
    "listHeader": "Role list",
    "loading": "Loading…",
    "emptyList": "No roles yet.",
    "badge": {
      "builtIn": "Built-in",
      "custom": "Custom"
    },
    "rowMeta": "{{permissions}} permissions · {{members}} members",
    "tablistAria": "Roles & Permissions tabs",
    "tabs": {
      "matrix": "Permission Matrix",
      "data": "Data Permissions"
    },
    "viewingLabel": "Viewing:",
    "overrideHintBefore": "Need a user-level override (additionalPermissions / deniedPermissions)? Go to ",
    "overrideHintAfter": " to configure per-user."
  },
  "conflictDiff": {
    "aria": "Server has newer version",
    "title": "Server has newer version",
    "description": "Another administrator updated these settings while you were editing. The server is now at version v{{currentVersion}} but your draft is based on v{{expectedVersion}}. Choose Re-load to discard your edits and start again from the server values, or Force Overwrite to replay your patch against the current version (audit-logged).",
    "noDiffs": "No detected diffs — the only difference is the version counter.",
    "columns": {
      "field": "Field",
      "yourValue": "Your value",
      "serverValue": "Server value"
    },
    "reload": "Re-load server values",
    "forceOverwrite": "Force Overwrite"
  },
  "stickySaveBar": {
    "aria": "Unsaved changes",
    "unsavedChanges": "You have unsaved changes.",
    "save": "Save Changes",
    "discard": "Discard",
    "saving": "Saving…",
    "readonly": "Read-only (no write permission)"
  },
  "useSettings": {
    "loadFailTitle": "Settings load failed",
    "loadFailFallback": "Could not load settings. Please retry.",
    "nothingToSaveTitle": "Nothing to save",
    "nothingToSaveDesc": "No fields changed since the last save.",
    "savedTitle": "Settings saved",
    "savedDesc_one": "Updated {{count}} field.",
    "savedDesc_other": "Updated {{count}} fields.",
    "conflictTitle": "Server has newer version",
    "conflictDesc": "Another administrator updated these settings. Review the diff.",
    "saveFailTitle": "Save failed",
    "saveFailFallback": "Could not save settings. Please retry."
  }
}